import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { FundClassification } from '@/models/common/FundClassification';
import { OpenFundRequestForEditDtoOut } from '@/models/out/OpenFundRequestForEditDtoOut';
import type { OpenFundEarlyLeadDtoOut } from '@/models/out/OpenFundEarlyLeadDtoOut';
import { useGetApi } from '@/hooks/UseApiCalls';
import IntroScreen from './IntroScreen';
import OpenFundSuccessScreen from './OpenFundSuccessScreen';
import OpenFundForm from './OpenFundForm';
import { mapRequestToFormData } from './utils/mapRequestToFormData';
import { buildExistingDocuments } from './utils/buildExistingDocuments';
import { readStoredOpenFundEarlyLead } from './openFundEarlyLeadStorage';
import { readReturnTo } from '@/helpers/returnTo';
import styles from './steps/steps.module.scss';

type PageState = 'intro' | 'form' | 'success';

const HATZDAKA_SITE_URL = 'https://www.hatzdaka.org';

interface OpenFundRequestPageProps {
  classification?: FundClassification.Welfare | FundClassification.Community;
}

const toFormClassification = (
  c?: FundClassification
): FundClassification.Welfare | FundClassification.Community => {
  if (c === FundClassification.Community || c === FundClassification.CampaignForCommunity) {
    return FundClassification.Community;
  }
  return FundClassification.Welfare;
};

const OpenFundRequestPage = ({ classification: classificationProp }: OpenFundRequestPageProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Set only by the personal area's own menu item (HAT-1370), never by a link from outside.
  const returnTo = readReturnTo(useLocation().state);

  const requestIdParam = searchParams.get('requestId');
  const token = searchParams.get('token') ?? undefined;
  const earlyLeadIdParam = searchParams.get('earlyLeadId') ?? undefined;
  const earlyLeadTokenParam = searchParams.get('earlyLeadToken') ?? undefined;
  const editRequestId = requestIdParam ? Number(requestIdParam) : undefined;
  const isEditMode = editRequestId != null && !isNaN(editRequestId) && !!token;

  const storedEarlyLead = !isEditMode ? readStoredOpenFundEarlyLead() : null;
  const earlyLeadId = earlyLeadIdParam ?? storedEarlyLead?.leadId;
  const earlyLeadToken = earlyLeadTokenParam ?? storedEarlyLead?.resumeToken;

  const { data: resumedEarlyLead } = useGetApi<OpenFundEarlyLeadDtoOut>(
    earlyLeadId && earlyLeadToken
      ? `funds/creation/open-fund-early-lead?leadId=${encodeURIComponent(earlyLeadId)}&token=${encodeURIComponent(earlyLeadToken)}`
      : '',
  );

  // Edit mode starts directly on the form; create mode starts on the intro screen.
  const [pageState, setPageState] = useState<PageState>(isEditMode ? 'form' : 'intro');
  const [open, setOpen] = useState(true);
  // When the applicant accepted the privacy policy on the intro screen. Stored with the request so
  // the office can show which wording was agreed to and when. Edit mode has no intro screen: that
  // consent was given when the request was first sent, and the server keeps it.
  const [privacyAcceptedAt, setPrivacyAcceptedAt] = useState<string | null>(null);

  const {
    data: editData,
    isLoading: isEditLoading,
    isError: isEditError,
  } = useGetApi<OpenFundRequestForEditDtoOut>(
    isEditMode ? `funds/creation/${editRequestId}/edit?token=${encodeURIComponent(token!)}` : ''
  );

  const classification = isEditMode
    ? toFormClassification(editData?.request.fundClassification)
    : classificationProp ?? FundClassification.Welfare;

  const initialValues = useMemo(
    () => (isEditMode && editData ? mapRequestToFormData(editData.request) : undefined),
    [isEditMode, editData]
  );

  const existingDocuments = useMemo(
    () => (isEditMode && editData ? buildExistingDocuments(editData.request) : undefined),
    [isEditMode, editData]
  );

  // Opened from the landing site (or from an emailed edit link), closing returns to the
  // landing site - also when the visitor happens to have an open personal-area session,
  // which used to send them to their own fund's page (SPR-1085). Opened from the personal
  // area's menu, it returns to the screen the coordinator left, and the form leaves no
  // history entry to come back to.
  const redirectAway = () => {
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }
    window.location.href = HATZDAKA_SITE_URL;
  };

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (value) return;
    redirectAway();
  };

  const handleSuccessClose = () => {
    setOpen(false);
    redirectAway();
  };

  const title =
    pageState === 'intro'
      ? t(`openFundRequest.pageTitle.${classification}`)
      : t(`openFundRequest.formTitle.${classification}`);

  const renderBody = () => {
    if (isEditMode && isEditLoading) {
      return <div style={{ padding: 24, textAlign: 'center' }}>{t('loading')}</div>;
    }

    if (isEditMode && (isEditError || !editData)) {
      return (
        <div style={{ padding: 24, textAlign: 'center', direction: 'rtl' }}>
          {t('openFundRequest.edit.invalidLink')}
        </div>
      );
    }

    if (pageState === 'intro') {
      return (
        <>
          {resumedEarlyLead && (
            <div className={styles.resumeBanner}>
              <p className="body-sm-regular">{t('openFundRequest.earlyLead.resumeBanner')}</p>
            </div>
          )}
          <IntroScreen
            classification={classification}
            onStart={(acceptedAt) => {
              setPrivacyAcceptedAt(acceptedAt);
              setPageState('form');
            }}
          />
        </>
      );
    }

    if (pageState === 'form') {
      return (
        <>
          {isEditMode && (
            <div
              style={{
                direction: 'rtl',
                textAlign: 'right',
                background: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: 6,
                padding: '8px 12px',
                margin: '0 auto 12px',
                maxWidth: 480,
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 600 }}>{t('openFundRequest.edit.bannerTitle')}</div>
              {editData?.incompleteReason && (
                <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{editData.incompleteReason}</div>
              )}
            </div>
          )}
          <OpenFundForm
            classification={classification}
            onSuccess={() => setPageState('success')}
            editRequestId={isEditMode ? editRequestId : undefined}
            editToken={isEditMode ? token : undefined}
            initialValues={initialValues}
            existingDocuments={existingDocuments}
            privacyAcceptedAt={privacyAcceptedAt}
            resumedEarlyLead={!isEditMode ? resumedEarlyLead ?? null : null}
          />
        </>
      );
    }

    return <OpenFundSuccessScreen onClose={handleSuccessClose} />;
  };

  return (
    <MainDialogLayout open={open} onOpenChange={handleOpenChange} title={title} contentClassName={styles.compactDialog}>
      {renderBody()}
    </MainDialogLayout>
  );
};

export default OpenFundRequestPage;
